import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { Dialog, DialogTitle, DialogContent, Box, Typography, CircularProgress } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import GameService from '../../../services/game.service';
import { Input, Select, MenuItem, TableRow } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { PLAYER_ICON_DEFAULT } from '../../../common/staticData';
import ForwardIcon from '@mui/icons-material/ForwardTwoTone';

const columns = [
    { id: 'index', label: 'Jersey', minWidth: 30 },
    { id: 'photo', label: 'Photo', minWidth: 40 },
    { id: 'name', label: 'Name', minWidth: 170 },
    { id: 'start_time', label: 'Start', minWidth: 100 },
    { id: 'end_time', label: 'End', minWidth: 100 },
    { id: 'totaltime', label: 'Total', minWidth: 100 },
    { id: 'startedstatus', label: 'Started', minWidth: 100 },
    { id: 'substituted', label: 'Substituted Player', minWidth: 100 }
];

const GameEditMinutesPage = ({ open, onClose, game, updateGameList, bTagging = false }) => {
    const [homeRows, setHomeRows] = useState([]);
    const [awayRows, setAwayRows] = useState([]);
    const [editRowId, setEditRowId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [isFirstTeam, setIsHomeTeam] = useState(true);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const teamsList = useSelector((state) => state.coach.teamsList);
    const playersList = useSelector((state) => state.coach.playersList);
    const [gamePlayers, setGamePlayers] = useState([]);
    const [tagTeamsList, setTagTeamsList] = useState([]);

    // Derive rows from the selected team
    const rows = isFirstTeam ? homeRows : awayRows;
    const setRows = isFirstTeam ? setHomeRows : setAwayRows;



    const loadPlayersMinutes = async () => {
        if (bTagging) {
            // Run API calls in parallel for better performance - load BOTH teams at once
            const [teamPlayersRes, homeMinutesRes, awayMinutesRes] = await Promise.all([
                GameService.getPlayersWithTagsForGame({ game_id: game.id }).catch(() => ({ home_team: [], away_team: [] })),
                GameService.getPlayersMinutes(game.id, game.home_team_id).catch(() => []),
                GameService.getPlayersMinutes(game.id, game.away_team_id).catch(() => [])
            ]);

            // Use team names from game object instead of making additional API calls
            setTagTeamsList([
                { id: game.home_team_id, name: game.home_team_name },
                { id: game.away_team_id, name: game.away_team_name }
            ]);

            // Process game players - combine home and away (only players with tags)
            const tmp_arr = [
                ...teamPlayersRes.home_team.map((p) => ({
                    ...p, player_full_name: p.player_full_name || (p.player_first_name + " " + p.player_last_name), checked: true
                })),
                ...teamPlayersRes.away_team.map((p) => ({
                    ...p, player_full_name: p.player_full_name || (p.player_first_name + " " + p.player_last_name), checked: true
                }))
            ];
            setGamePlayers(tmp_arr);

            // Helper function to merge players with their minutes data
            const mergePlayersWithMinutes = (players, minutesRes, teamId) => {
                const minutesMap = new Map(minutesRes.map(m => [m.player_id, m]));
                return players.map((player) => {
                    const existingMinutes = minutesMap.get(player.player_id);
                    if (existingMinutes) {
                        return {
                            ...existingMinutes,
                            jersey_number: existingMinutes.jersey_number || player.player_jersey_number || 0
                        };
                    }
                    return {
                        id: null,
                        game_id: game.id,
                        team_id: player.team_id || teamId,
                        player_id: player.player_id,
                        start_time: 0,
                        end_time: 90,
                        player_substitution_id: null,
                        jersey_number: player.player_jersey_number || 0,
                        started: true
                    };
                });
            };

            // Merge and set both teams' rows
            setHomeRows(mergePlayersWithMinutes(teamPlayersRes.home_team, homeMinutesRes, game.home_team_id));
            setAwayRows(mergePlayersWithMinutes(teamPlayersRes.away_team, awayMinutesRes, game.away_team_id));
        }
        else {
            // Coach/games page - use same logic as tagging but filter by user's team access
            const hasHomeAccess = filteredTeams.some(t => t.team_id === game.home_team_id);
            const hasAwayAccess = filteredTeams.some(t => t.team_id === game.away_team_id);
            if (!hasHomeAccess && !hasAwayAccess) return;

            // Set initial team selection based on user's access
            if (!hasHomeAccess && hasAwayAccess) {
                setIsHomeTeam(false);
            }

            const [teamPlayersRes, homeMinutesRes, awayMinutesRes] = await Promise.all([
                GameService.getPlayersWithTagsForGame({ game_id: game.id }).catch(() => ({ home_team: [], away_team: [] })),
                hasHomeAccess ? GameService.getPlayersMinutes(game.id, game.home_team_id).catch(() => []) : Promise.resolve([]),
                hasAwayAccess ? GameService.getPlayersMinutes(game.id, game.away_team_id).catch(() => []) : Promise.resolve([])
            ]);

            // Set team names for display (only teams user has access to)
            const accessibleTeams = [];
            if (hasHomeAccess) accessibleTeams.push({ id: game.home_team_id, name: game.home_team_name });
            if (hasAwayAccess) accessibleTeams.push({ id: game.away_team_id, name: game.away_team_name });
            setTagTeamsList(accessibleTeams);

            // Process game players - only include teams user has access to
            const tmp_arr = [
                ...(hasHomeAccess ? teamPlayersRes.home_team : []).map((p) => ({
                    ...p, player_full_name: p.player_full_name || (p.player_first_name + " " + p.player_last_name), checked: true
                })),
                ...(hasAwayAccess ? teamPlayersRes.away_team : []).map((p) => ({
                    ...p, player_full_name: p.player_full_name || (p.player_first_name + " " + p.player_last_name), checked: true
                }))
            ];
            setGamePlayers(tmp_arr);

            // Helper function to merge players with their minutes data
            const mergePlayersWithMinutes = (players, minutesRes, teamId) => {
                const minutesMap = new Map(minutesRes.map(m => [m.player_id, m]));
                return players.map((player) => {
                    const existingMinutes = minutesMap.get(player.player_id);
                    if (existingMinutes) {
                        return {
                            ...existingMinutes,
                            jersey_number: existingMinutes.jersey_number || player.player_jersey_number || 0
                        };
                    }
                    return {
                        id: null,
                        game_id: game.id,
                        team_id: player.team_id || teamId,
                        player_id: player.player_id,
                        start_time: 0,
                        end_time: 90,
                        player_substitution_id: null,
                        jersey_number: player.player_jersey_number || 0,
                        started: true
                    };
                });
            };

            // Merge and set only teams user has access to
            if (hasHomeAccess) setHomeRows(mergePlayersWithMinutes(teamPlayersRes.home_team, homeMinutesRes, game.home_team_id));
            if (hasAwayAccess) setAwayRows(mergePlayersWithMinutes(teamPlayersRes.away_team, awayMinutesRes, game.away_team_id));
        }
    }


    useEffect(() => {
        // if (bTagging) {
        //     // console.log("filtered: ", tagTeamsList);
        //     // const filtered = tagTeamsList.filter(
        //     //     (item) => item.season_name === game.season_name && item.league_name === game.league_name && (item.team_id === game.home_team_id || item.team_id === game.away_team_id)
        //     // );
        //     // setFilteredTeams(filtered)

        // }
        // else {
        const filtered = teamsList.filter(
            (item) => item.season_name === game.season_name && item.league_name === game.league_name && (item.team_id === game.home_team_id || item.team_id === game.away_team_id)
        );
        setFilteredTeams(filtered)

        // }


    }, [game.away_team_id, game.home_team_id, game.league_name, game.season_name, teamsList, tagTeamsList])

    useEffect(() => {
        if (!open) return;
        (async () => {
            await loadPlayersMinutes()
        })();
    }, [game, open, teamsList, bTagging, filteredTeams]);

    const update = async (value) => {
        if (isSaving) return; // Prevent double saves

        // Update the correct team's rows based on current selection
        if (isFirstTeam) {
            setHomeRows(prev => {
                const newRows = [...prev];
                newRows[editRowId] = value;
                return newRows;
            });
        } else {
            setAwayRows(prev => {
                const newRows = [...prev];
                newRows[editRowId] = value;
                return newRows;
            });
        }
        setEditRowId(null)
        setEditValues({})
        setIsSaving(true);

        // Ensure numeric values are properly formatted for the API
        const sanitizedValue = {
            ...value,
            game_id: parseInt(value.game_id, 10),
            team_id: parseInt(value.team_id, 10),
            player_id: parseInt(value.player_id, 10),
            start_time: parseInt(value.start_time, 10) || 0,
            end_time: parseInt(value.end_time, 10) || 0,
            jersey_number: parseInt(value.jersey_number, 10) || 0,
            player_substitution_id: value.player_substitution_id || null
        };

        try {
            // If no id, create new record; otherwise update existing
            if (value.id) {
                await GameService.editPlayersMinutes(value.id, sanitizedValue)
            } else {
                await GameService.addPlayersMinutes(sanitizedValue)
            }
        } catch (error) {
            console.error('Failed to save player minutes:', error.response?.data || error.message);
        }
        await loadPlayersMinutes()
        setIsSaving(false);
        if (updateGameList) updateGameList()
    }

    const getPlayer = useCallback(
        (id) => {
            // Try gamePlayers first (populated from getGameTeamPlayers), then fall back to playersList
            return gamePlayers.find((item) => item.player_id == id)
                || playersList.find((item) => item.player_id == id);
        },
        [playersList, gamePlayers]
    );

    const handleEditClick = async (rowId) => {
        if (rowId === editRowId) return
        // Save pending edits before switching to a new row
        if (editRowId !== null && Object.keys(editValues).length > 0) {
            await update(editValues);
        }
        setEditRowId(rowId);
        const row = rows.find((r, idx) => idx === rowId);
        setEditValues({ ...row });
    };

    const handleInputChange = (event, index) => {
        const { name, value } = event.target;
        // Convert numeric fields to numbers (input values are always strings)
        const numericValue = ['start_time', 'end_time', 'jersey_number'].includes(name)
            ? parseInt(value, 10) || 0
            : value;

        // Update state with the new value
        setEditValues((prevValues) => {
            const newValues = { ...prevValues, [name]: numericValue };
            // Compute started based on the current start_time value
            const currentStartTime = name === 'start_time' ? numericValue : prevValues.start_time;
            newValues.started = currentStartTime === 0;
            // Only set substitution if player didn't start
            if (name === 'player_substitution_id') {
                newValues.player_substitution_id = !newValues.started ? value : null;
            }
            return newValues;
        });

        if (name === "player_substitution_id") {
            const started = editValues.start_time === 0;
            update({
                ...editValues,
                [name]: value,
                started,
                player_substitution_id: !started ? value : null,
            });
        }
    };

    const handleSwitch = async (bChanged) => {
        if (bChanged !== isFirstTeam) {
            // Save pending edits before switching teams
            if (editRowId !== null && Object.keys(editValues).length > 0) {
                await update(editValues);
            }
            setIsHomeTeam(bChanged);
        }
    }

    const handleBlur = async () => {
        // Auto-save when input loses focus
        if (editRowId !== null && Object.keys(editValues).length > 0) {
            await update(editValues);
        }
    }

    // Save all players minutes when closing the modal (both teams)
    const saveAllPlayersMinutes = async () => {
        setIsSaving(true);
        try {
            // Save both home and away team rows
            const allRows = [...homeRows, ...awayRows];
            for (const row of allRows) {
                const sanitizedValue = {
                    game_id: parseInt(row.game_id || game.id, 10),
                    team_id: parseInt(row.team_id, 10),
                    player_id: parseInt(row.player_id, 10),
                    start_time: parseInt(row.start_time, 10) || 0,
                    end_time: parseInt(row.end_time, 10) || 0,
                    jersey_number: parseInt(row.jersey_number, 10) || 0,
                    player_substitution_id: row.player_substitution_id || null
                };

                if (row.id) {
                    // Update existing record
                    await GameService.editPlayersMinutes(row.id, sanitizedValue);
                } else {
                    // Add new record
                    await GameService.addPlayersMinutes(sanitizedValue);
                }
            }
        } catch (error) {
            console.error('Failed to save all player minutes:', error.response?.data || error.message);
        }
        setIsSaving(false);
        if (updateGameList) updateGameList();
    }

    const handleClose = async (event, reason) => {
        if (reason === "escapeKeyDown" && editRowId !== null) {
            setEditRowId(null)
            setEditValues({})
            return
        }
        // Save pending edits before closing
        if (editRowId !== null && Object.keys(editValues).length > 0) {
            await update(editValues);
        }
        // Save all players minutes when closing
        await saveAllPlayersMinutes();
        onClose();
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    {/* Show single team name when only one team */}
                    {tagTeamsList.length === 1 && (
                        <Typography component="div" sx={{ flexGrow: 1, fontSize: 20, fontWeight: 'bold', color: '#17375E' }}>
                            {tagTeamsList[0].name}
                        </Typography>
                    )}
                    {/* Show team selector when multiple teams */}
                    {tagTeamsList.length > 1 && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: "8px" }}>
                        <Box
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backgroundColor: !isFirstTeam ? '#17375E' : 'transparent',
                                border: !isFirstTeam ? '2px solid #17375E' : '2px solid #ccc',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => handleSwitch(false)}
                        >
                            <Typography sx={{
                                color: !isFirstTeam ? 'white' : '#666',
                                fontWeight: !isFirstTeam ? 'bold' : 'normal',
                                fontSize: '16px'
                            }}>
                                {tagTeamsList[0].name}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                sx={{ 'svg path:last-of-type': { fill: 'black' }, 'svg path:first-of-type': { fill: isFirstTeam ? '#ffffff' : '#17375E', opacity: 1 }, cursor: 'pointer' }}
                                onClick={() => handleSwitch(false)}
                            >
                                <ForwardIcon sx={{ transform: 'rotate(180deg)' }} fontSize="large" />
                            </Box>
                            <Box
                                sx={{ 'svg path:last-of-type': { fill: 'black' }, 'svg path:first-of-type': { fill: isFirstTeam ? '#17375E' : '#ffffff', opacity: 1 }, cursor: 'pointer' }}
                                onClick={() => handleSwitch(true)}
                            >
                                <ForwardIcon fontSize="large" />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backgroundColor: isFirstTeam ? '#17375E' : 'transparent',
                                border: isFirstTeam ? '2px solid #17375E' : '2px solid #ccc',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => handleSwitch(true)}
                        >
                            <Typography sx={{
                                color: isFirstTeam ? 'white' : '#666',
                                fontWeight: isFirstTeam ? 'bold' : 'normal',
                                fontSize: '16px'
                            }}>
                                {tagTeamsList[1].name}
                            </Typography>
                        </Box>
                    </Box>

                    }
                    {isSaving && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
                            <CircularProgress size={16} />
                            <Typography sx={{ fontSize: '14px', color: '#666' }}>Saving...</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', cursor: 'pointer' }} onClick={handleClose}>
                        <CloseIcon sx={{ width: '18px', height: '18px' }} />
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', overflowY: 'unset' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ overflowY: 'auto' }}>
                        {rows.map((row, index) => (
                            <TableRow hover role="checkbox" tabIndex={-1} key={index} onClick={() => handleEditClick(index)} onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    update(editValues); // Replace updateFunction with the actual function you want to call
                                }
                            }}>
                                <TableCell sx={{ height: 30 }}>
                                    {editRowId === index ? (
                                        <Input sx={{ height: 30 }} name="jersey_number" type="number" value={editValues.jersey_number} onChange={handleInputChange} onBlur={handleBlur} fullWidth />
                                    ) : (
                                        row.jersey_number || getPlayer(row.player_id)?.player_jersey_number || 0
                                    )}
                                </TableCell>
                                <TableCell sx={{ height: 40 }}>
                                    <img
                                        width={40}
                                        src={
                                            getPlayer(row.player_id)?.player_image
                                                ? getPlayer(row.player_id)?.player_image.includes(process.env.REACT_APP_S3_URI)
                                                    ? getPlayer(row.player_id)?.player_image
                                                    : process.env.REACT_APP_S3_URI + getPlayer(row.player_id)?.player_image
                                                : PLAYER_ICON_DEFAULT
                                        }
                                        alt="Player"
                                    />
                                </TableCell>
                                {/* <TableCell sx={{ height: 30 }}>{JSON.stringify(getPlayer(row.player_id))}</TableCell> */}
                                <TableCell sx={{ height: 30 }}>{getPlayer(row.player_id)?.player_full_name} ({getPlayer(row.player_id)?.player_position_short_name})</TableCell>
                                <TableCell sx={{ height: 30 }}>
                                    {editRowId === index ? (
                                        <Input sx={{ height: 30 }} name="start_time" type="number" value={editValues.start_time} onChange={handleInputChange} onBlur={handleBlur} fullWidth />
                                    ) : (
                                        row.start_time
                                    )}
                                </TableCell>
                                <TableCell sx={{ height: 30 }}>
                                    {editRowId === index ? (
                                        <Input sx={{ height: 30 }} name="end_time" type="number" value={editValues.end_time} onChange={handleInputChange} onBlur={handleBlur} fullWidth />
                                    ) : (
                                        row.end_time
                                    )}
                                </TableCell>
                                <TableCell sx={{ height: 30 }}>{row.end_time - row.start_time}</TableCell>
                                <TableCell sx={{ height: 30 }}>
                                    <CircleIcon fontSize="small" sx={{ color: row.started ? 'green' : 'red' }} />
                                </TableCell>
                                <TableCell sx={{ height: 30 }}>
                                    {editRowId === index && !editValues.started ? (
                                        <Select sx={{ height: 30 }} name="player_substitution_id" value={editValues.player_substitution_id ?? ''} onChange={handleInputChange} fullWidth>
                                            {
                                                row.player_substitution_id &&
                                                <MenuItem key={`current-${row.player_substitution_id}`} value={row.player_substitution_id}>{getPlayer(row.player_substitution_id)?.player_full_name} #{getPlayer(row.player_substitution_id)?.player_jersey_number}</MenuItem>
                                            }
                                            {rows.filter(row => !rows.map(r => r.player_substitution_id).includes(row.player_id) && row.player_id !== editValues.player_id).map((row) => (
                                                <MenuItem key={row.player_id} value={row.player_id}>{getPlayer(row.player_id)?.player_full_name} #{getPlayer(row.player_id)?.player_jersey_number}</MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        row.player_substitution_id ? `${getPlayer(row.player_substitution_id)?.player_full_name}  #${getPlayer(row.player_substitution_id)?.player_jersey_number} (${getPlayer(row.player_substitution_id)?.player_position_short_name})` : ""
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog >
    );
};

export default GameEditMinutesPage;
